import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Eye, TrendingUp, Heart, Zap } from "lucide-react";

const ICON_MAP = {
  flame: Flame,
  eye: Eye,
  "trending-up": TrendingUp,
  heart: Heart,
  zap: Zap,
};

const INTENSITY_STYLES = {
  hot:      "border-red-500/40 bg-red-500/10 text-red-200 [&_svg]:text-red-400",
  warm:     "border-amber-500/40 bg-amber-500/10 text-amber-200 [&_svg]:text-amber-400",
  active:   "border-emerald-500/30 bg-emerald-500/5 text-emerald-200 [&_svg]:text-emerald-400",
  trending: "border-blue-500/30 bg-blue-500/5 text-blue-200 [&_svg]:text-blue-400",
  geo:      "border-zinc-700 bg-zinc-900/70 text-zinc-200 [&_svg]:text-zinc-400",
};

const PULSE_INTENSITIES = new Set(["hot", "warm"]);

/**
 * Phase 61 — SocialProofPills.
 *
 * Renders 0-4 trust pills based on real activity from the backend.
 * - Never randomized. Never fabricated. If backend returns no signals, renders nothing.
 * - Subtle pulse on "hot"/"warm" intensities only — premium, not spammy.
 * - Server-rendered placeholder reserves layout space → zero CLS.
 *
 * Props:
 *   listingId: string (required) — listing UUID to fetch /activity for
 *   locale:    "de" | "fr" | "it" | "en" (default "de")
 *   max:       number — cap pills rendered (default 3)
 */
export default function SocialProofPills({ listingId, locale = "de", max = 3 }) {
  const [signals, setSignals] = useState(null); // null = loading, [] = none

  useEffect(() => {
    if (!listingId) return;
    let alive = true;
    api.listingActivity(listingId)
      .then((res) => alive && setSignals(Array.isArray(res?.signals) ? res.signals : []))
      .catch(() => alive && setSignals([]));
    return () => { alive = false; };
  }, [listingId]);

  // Pre-render an invisible placeholder with reserved height so that when the
  // fetch resolves and pills appear, there is no layout shift (CLS = 0).
  // The placeholder is `aria-hidden` so screen readers ignore it.
  if (signals === null) {
    return <div className="mt-3 h-7" aria-hidden="true" data-testid="social-proof-placeholder" />;
  }
  if (signals.length === 0) {
    return null;
  }

  const shown = signals.slice(0, max);

  return (
    <div
      className="mt-3 flex flex-wrap gap-2"
      data-testid="social-proof-pills"
      aria-label="Aktivität"
    >
      {shown.map((sig, idx) => {
        const Icon = ICON_MAP[sig.icon] || Flame;
        const tone = INTENSITY_STYLES[sig.intensity] || INTENSITY_STYLES.active;
        const pulse = PULSE_INTENSITIES.has(sig.intensity);
        const label = sig.labels?.[locale] || sig.labels?.de || "";
        return (
          <span
            key={`${sig.kind}-${idx}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold tracking-wide ${tone} ${pulse ? "social-pill-pulse" : ""}`}
            data-testid={`social-pill-${sig.kind}`}
            data-intensity={sig.intensity}
            title={label}
          >
            <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            <span>{label}</span>
          </span>
        );
      })}
    </div>
  );
}
